import { useState, useEffect } from 'react';

interface Course {
  id: string;
  title: string;
  description: string;
  instructor_name: string;
  teacher_name?: string;
  teacher_id: string;
  duration: string;
  level: string;
  enrolled: boolean;
  progress?: number;
  start_date?: string;
  end_date?: string;
  schedule?: string;
  student_count?: number;
  created_at?: string;
  status?: string;
}

export default function Courses() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  // Fetch courses from API
  const fetchCourses = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-courses`, {
        method: 'GET',
        headers: {
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCourses(data || []);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Không thể tải danh sách khóa học');
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError('Không thể tải dữ liệu khóa học. Vui lòng thử lại sau.');
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async (courseId: string) => {
    setLoadingAssignments(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/rest/v1/assignments?course_id=eq.${courseId}&select=*`, {
        headers: {
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAssignments(data || []);
      } else {
        setAssignments([]);
      }
    } catch (err) {
      console.error('Error fetching assignments:', err);
      setAssignments([]);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const handleViewAssignments = (course: Course) => {
    setSelectedCourse(course);
    setShowAssignmentsModal(true);
    fetchAssignments(course.id);
  };

  const getStatusInfo = (course: Course) => {
    const now = new Date();
    const startDate = course.start_date ? new Date(course.start_date) : null;
    const endDate = course.end_date ? new Date(course.end_date) : null;

    if (startDate && endDate) {
      if (now < startDate) {
        return {
          text: 'Chưa bắt đầu',
          color: 'bg-gray-100 text-gray-800',
          icon: 'ri-time-line'
        };
      } else if (now >= startDate && now <= endDate) {
        return {
          text: 'Đang học',
          color: 'bg-green-100 text-green-800',
          icon: 'ri-play-line'
        };
      } else {
        return {
          text: 'Đã hoàn thành',
          color: 'bg-blue-100 text-blue-800',
          icon: 'ri-check-line'
        };
      }
    }

    switch (course.status) {
      case 'active':
        return {
          text: 'Đang mở',
          color: 'bg-green-100 text-green-800',
          icon: 'ri-play-line'
        };
      case 'draft':
        return {
          text: 'Bản nháp',
          color: 'bg-yellow-100 text-yellow-800',
          icon: 'ri-draft-line'
        };
      case 'completed':
        return {
          text: 'Đã hoàn thành',
          color: 'bg-blue-100 text-blue-800',
          icon: 'ri-check-line'
        };
      default:
        return {
          text: 'Đang mở',
          color: 'bg-green-100 text-green-800',
          icon: 'ri-play-line'
        };
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-2 text-gray-500">
          <i className="ri-loader-4-line animate-spin"></i>
          <span>Đang tải khóa học...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        <div className="flex items-center">
          <i className="ri-error-warning-line mr-2"></i>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Khóa học của tôi</h2>
          <p className="text-sm text-gray-500 mt-1">Tổng cộng: {courses.length} khóa học</p>
        </div>
        <button
          onClick={fetchCourses}
          className="flex items-center px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-all whitespace-nowrap cursor-pointer"
        >
          <i className="ri-refresh-line mr-2"></i>
          Làm mới
        </button>
      </div>

      {courses.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
          <i className="ri-book-line text-4xl mb-3"></i>
          <p className="text-sm">Bạn chưa được thêm vào khóa học nào</p>
          <p className="text-xs mt-1">Vui lòng liên hệ admin để được thêm vào khóa học</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => {
            const statusInfo = getStatusInfo(course);
            return (
              <div key={course.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 flex-1">{course.title}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ml-2 whitespace-nowrap ${statusInfo.color}`}>
                      {statusInfo.text}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">{course.description}</p>
                  
                  {/* Thông tin giáo viên */}
                  <div className="bg-blue-50 rounded-lg p-3 mb-4">
                    <div className="flex items-center text-sm text-blue-900">
                      <i className="ri-user-line mr-2"></i>
                      <span className="font-medium">Giáo viên: {course.teacher_name || course.instructor_name || 'Chưa có giáo viên'}</span>
                    </div>
                  </div>

                  {/* Thông tin thời gian và lịch học */}
                  {(course.start_date || course.end_date || course.schedule) && (
                    <div className="bg-gray-50 rounded-lg p-3 mb-4 space-y-2">
                      {course.start_date && course.end_date && (
                        <div className="flex items-center text-sm text-gray-600">
                          <i className="ri-calendar-line mr-2"></i>
                          {new Date(course.start_date).toLocaleDateString('vi-VN')} - {new Date(course.end_date).toLocaleDateString('vi-VN')}
                        </div>
                      )}
                      {course.schedule && (
                        <div className="flex items-center text-sm text-gray-600">
                          <i className="ri-time-line mr-2"></i>
                          {course.schedule}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tiến độ học tập */}
                  {course.progress !== undefined && (
                    <div className="mb-4">
                      <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>Tiến độ</span>
                        <span>{course.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{ width: `${course.progress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <button 
                      onClick={() => handleViewAssignments(course)}
                      className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-all whitespace-nowrap cursor-pointer flex items-center justify-center"
                    >
                      <i className="ri-file-list-line mr-2"></i>
                      Xem bài tập
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Assignments Modal */}
      {showAssignmentsModal && selectedCourse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Bài tập - {selectedCourse.title}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Giáo viên: {selectedCourse.teacher_name || selectedCourse.instructor_name || 'Chưa có giáo viên'}
                  </p>
                </div>
                <button
                  onClick={() => setShowAssignmentsModal(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer w-8 h-8 flex items-center justify-center"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6">
              {loadingAssignments ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex items-center space-x-2 text-gray-500">
                    <i className="ri-loader-4-line animate-spin"></i>
                    <span>Đang tải bài tập...</span>
                  </div>
                </div>
              ) : assignments.length === 0 ? (
                <div className="text-center py-12">
                  <i className="ri-file-list-line text-4xl text-gray-300 mb-3"></i>
                  <p className="text-gray-500">Chưa có bài tập nào</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {assignments.map((assignment) => {
                    const dueDate = new Date(assignment.due_date);
                    const now = new Date();
                    const isOverdue = dueDate < now;
                    const daysLeft = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

                    return (
                      <div key={assignment.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">
                              {assignment.title}
                            </h3>
                            <p className="text-sm text-gray-600 line-clamp-2">
                              {assignment.description}
                            </p>
                          </div>
                          <span className={`px-3 py-1 text-xs font-medium rounded-full ml-3 whitespace-nowrap ${
                            isOverdue 
                              ? 'bg-red-100 text-red-800' 
                              : daysLeft <= 3 
                              ? 'bg-yellow-100 text-yellow-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {isOverdue ? 'Quá hạn' : daysLeft <= 3 ? 'Sắp hết hạn' : 'Còn hạn'}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div className="flex items-center text-sm text-gray-600">
                            <i className="ri-calendar-line mr-2"></i>
                            <span>Hạn nộp: {dueDate.toLocaleDateString('vi-VN')}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <i className="ri-trophy-line mr-2"></i>
                            <span>Điểm tối đa: {assignment.max_score}</span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                          <div className="flex items-center text-sm text-gray-500">
                            <i className="ri-time-line mr-1"></i>
                            {isOverdue 
                              ? `Quá hạn ${Math.abs(daysLeft)} ngày` 
                              : `Còn ${daysLeft} ngày`
                            }
                          </div>
                          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm whitespace-nowrap cursor-pointer">
                            <i className="ri-file-upload-line mr-2"></i>
                            Nộp bài
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
